import type { Queryable } from "@livestore/livestore";
import { useQuery } from "@livestore/react";

/**
 * Renders the value of a Queryable using a render function.
 * This function can use hooks like useMemo etc within
 * @example
 * ```tsx
 * <Queryable query={isEnabled$}>
 *   {(value) => <div data-loc="phosphor-blog/content/_sources/autocomplete-demo/components/Queryable.tsx:10:23">{value ? "Enabled" : "Disabled"}</div>}
 * </Queryable>
 * ```
 * @param props.query - The Queryable to subscribe to.
 * @param props.children - The function to render the value.
 * @returns The rendered value.
 */
export function Queryable<T>(props: {
  query: Queryable<T>;
  children: (value: T) => React.ReactNode;
}): React.ReactNode;
/**
 * @example
 * ```tsx
 * // Render as text
 * <Queryable query={displayName$} />
 * ```
 * @param props.query - The Queryable to subscribe to.
 * @returns The rendered value.
 */
export function Queryable(props: {
  query: Queryable<React.ReactNode | string | number | boolean | null | undefined>;
}): React.ReactNode;
export function Queryable(props: {
  query: Queryable<any>;
  children?: (value: any) => React.ReactNode;
}) {
  const value = useQuery(props.query as any);
  return <>{typeof props.children === "function" ? props.children(value) : (value as any)}</>;
}
